"use client"

import * as React from "react"
import { MapPin, List, Filter, Settings, ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import UserMenu from "./UserMenu"

interface NavPanelProps {
    isDarkMode: boolean

    // Nav state
    activeTab: 'list' | 'filters' | null
    onTabChange: (tab: 'list' | 'filters' | null) => void

    // User menu
    onOpenMyShops: () => void
    onOpenSettings: () => void
    onMenuStateChange: (isOpen: boolean, closeFn?: (() => void) | null) => void

    // Filter state
    filterCount?: number

    // Content
    children?: React.ReactNode

    className?: string
}

/**
 * Navigation panel for desktop/tablet - Google Maps style.
 * Contains logo, nav tabs, and content area.
 * On desktop: Always visible, can be collapsed.
 * On tablet: Overlay panel.
 */
export default function NavPanel({
    isDarkMode,
    activeTab,
    onTabChange,
    onOpenMyShops,
    onOpenSettings,
    onMenuStateChange,
    filterCount = 0,
    children,
    className,
}: NavPanelProps) {
    const isDesktop = useMediaQuery("(min-width: 1024px)")
    const isTablet = useMediaQuery("(min-width: 768px)")
    const [isCollapsed, setIsCollapsed] = React.useState(false)

    // Don't render on mobile
    if (!isTablet) return null

    const panelWidth = isCollapsed ? 64 : (isDesktop ? 400 : 320)
    const isOpen = activeTab !== null

    return (
        <>
            {/* Backdrop for tablet only when expanded */}
            {!isDesktop && isOpen && !isCollapsed && (
                <div
                    className="fixed inset-0 z-40 bg-black/40"
                    onClick={() => onTabChange(null)}
                />
            )}

            {/* Panel */}
            <div
                className={cn(
                    "fixed left-0 top-0 z-50 h-full flex flex-col transition-all duration-300 ease-in-out",
                    isDarkMode
                        ? "bg-gray-900 border-r border-gray-800"
                        : "bg-white border-r border-gray-200",
                    className
                )}
                style={{ width: panelWidth }}
            >
                {/* Header with logo */}
                <div className={cn(
                    "flex items-center justify-between px-4 h-16 shrink-0 border-b",
                    isDarkMode ? "border-gray-800" : "border-gray-200"
                )}>
                    {!isCollapsed && (
                        <div className="flex items-center gap-2">
                            <MapPin className={cn(
                                "h-6 w-6",
                                isDarkMode ? "text-blue-400" : "text-blue-600"
                            )} />
                            <span className={cn(
                                "font-bold text-lg",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                LoppeHub
                            </span>
                        </div>
                    )}

                    {/* Collapse button (desktop only) */}
                    {isDesktop && (
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className={cn(
                                "p-2 rounded-lg transition-colors",
                                isDarkMode
                                    ? "hover:bg-gray-800 text-gray-400"
                                    : "hover:bg-gray-100 text-gray-500"
                            )}
                        >
                            {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                        </button>
                    )}
                </div>

                {/* Navigation tabs */}
                <div className={cn(
                    "flex gap-1 p-2 border-b shrink-0",
                    isDarkMode ? "border-gray-800" : "border-gray-200",
                    isCollapsed && "flex-col"
                )}>
                    {/* List tab */}
                    <button
                        onClick={() => onTabChange(activeTab === 'list' ? null : 'list')}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
                            isCollapsed && "justify-center px-2",
                            activeTab === 'list'
                                ? isDarkMode
                                    ? "bg-blue-600/20 text-blue-400"
                                    : "bg-blue-50 text-blue-600"
                                : isDarkMode
                                    ? "text-gray-400 hover:bg-gray-800 hover:text-white"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        )}
                        title="Shop List"
                    >
                        <List className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span className="font-medium">Shops</span>}
                    </button>

                    {/* Filter tab */}
                    <button
                        onClick={() => onTabChange(activeTab === 'filters' ? null : 'filters')}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors relative",
                            isCollapsed && "justify-center px-2",
                            activeTab === 'filters'
                                ? isDarkMode
                                    ? "bg-blue-600/20 text-blue-400"
                                    : "bg-blue-50 text-blue-600"
                                : isDarkMode
                                    ? "text-gray-400 hover:bg-gray-800 hover:text-white"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        )}
                        title="Filters"
                    >
                        <Filter className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span className="font-medium">Filters</span>}
                        {filterCount > 0 && (
                            <span className={cn(
                                "absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-xs font-bold",
                                isDarkMode ? "bg-blue-500 text-white" : "bg-blue-600 text-white"
                            )}>
                                {filterCount}
                            </span>
                        )}
                    </button>

                    {/* Settings */}
                    <button
                        onClick={onOpenSettings}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors",
                            isCollapsed && "justify-center px-2",
                            isDarkMode
                                ? "text-gray-400 hover:bg-gray-800 hover:text-white"
                                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        )}
                        title="Settings"
                    >
                        <Settings className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span className="font-medium">Settings</span>}
                    </button>
                </div>

                {/* Content area */}
                {!isCollapsed && (
                    <div className="flex-1 overflow-y-auto">
                        {children}
                    </div>
                )}

                {/* User menu at bottom */}
                <div className={cn(
                    "mt-auto p-3 border-t shrink-0",
                    isDarkMode ? "border-gray-800" : "border-gray-200"
                )}>
                    <UserMenu
                        isDarkMode={isDarkMode}
                        onOpenMyShops={onOpenMyShops}
                        onOpenSettings={onOpenSettings}
                        onMenuStateChange={onMenuStateChange}
                    />
                </div>
            </div>
        </>
    )
}
